import { Dictionary } from 'lodash';
import { key as t_key } from 'core-ts-types';
import * as _4 from 'lodash';

type token_type<A> = number | string | A;

export interface IStrategy<A> {
  size(): number;
  includes(token: t_key): boolean;
  /**
   * @alias getByKey
   * allows Strategy to map to Metasource in ORM
   */
  get(token: string): A;
  getByKey(token: string): A;
  getByOrdinal(token: number): A;
  getByValue(token: A): A;
  getKeyByValue(token: A): string;
  getOrdinalByValue(token: A): number;
  isEqual(first: token_type<A>, second: token_type<A>): boolean;
  executionContext(token: token_type<A>): IStrategyExecutionContext<A>;
  insert(key: string, value: A): this;
  replace(key: string, value: A): this;
  move(oldKey: string, newKey: string): this;
  remove(key: string): this;
  clear(): this;
  fallback(strategy: string | A): this;
  map<B>(func: (value: A, key?: string) => B): B[];
}

export interface IStrategySafe<A, S extends string> extends IStrategy<A> {
  includes(token: S): boolean;
  get(token: S): A;
  getByKey(token: S): A;
  insert(key: S, value: A): this;
  replace(key: S, value: A): this;
  move(oldKey: S, newKey: string): this;
  remove(key: S): this;
  fallback(strategy: S | A): this;
}

interface IStrategyRecord<A> {
  ordinal: number;
  key: string;
  value: A;
}

export interface IStrategyExecutionContext<A> {
  map(fn: IExecutionContext<A>): this;
  value<B, C>(value: B): C;
}

interface IExecutionContext<A> {
  (algorithm: A, value: any): any;
}

export class StrategyExecutionContext<A> implements IStrategyExecutionContext<A> {
  private _pipeline: IExecutionContext<A>[] = [];

  constructor(private _algorithm: A) {}

  map(fn: IExecutionContext<A>): this {
    this._pipeline.push(fn);
    return this;
  }

  value<B, C>(value: B): C {
    return _4.reduce(
      this._pipeline,
      (res: any, fn: IExecutionContext<A>) => {
        return fn(this._algorithm, res);
      },
      value
    );
  }
}

/**
 * Strategy
 *
 * @description represents a set of algorithms to choose at run-time
 */
export class Strategy<A> implements IStrategy<A> {
  private __db: Record<string, IStrategyRecord<A>> = {};
  private __fallback: IStrategyRecord<A>;

  constructor(keys: Dictionary<A>) {
    Strategy._hideProperty(this, '__db');
    Strategy._hideProperty(this, '__fallback');

    _4.each(keys, (value, key: string) => {
      if (key.toLowerCase() === 'fallback') {
        this.fallback(value);
      } else {
        this.__insert(key, value);
      }
    });
  }

  size(): number {
    return _4.size(this.__db);
  }

  includes(token: t_key): boolean {
    if (typeof token === 'string') {
      return this.__db.hasOwnProperty(token);
    } else if (typeof token === 'number') {
      return !!Strategy._getByUnsafe<A>(this.__db, token);
    } else {
      return false;
    }
  }

  get(token: string): A {
    return this._getBy(token).value;
  }

  getByKey(token: string): A {
    return this._getBy(token).value;
  }

  getByOrdinal(token: number): A {
    return this._getBy(token).value;
  }

  getByValue(token: A): A {
    return this._getBy(token).value;
  }

  getKeyByValue(token: A): string {
    return this._getBy(token).key;
  }

  getOrdinalByValue(token: A): number {
    return this._getBy(token).ordinal;
  }

  protected _getBy(token: token_type<A>): IStrategyRecord<A> {
    return Strategy._getByUnsafe<A>(this.__db, token) || this.__fallback;
  }

  private static _getByUnsafe<A>(
    db: Record<string, IStrategyRecord<A>>,
    token: token_type<A>
  ): IStrategyRecord<A> | undefined {
    if (typeof token === 'number') {
      return _4.find(db, ({ ordinal }) => {
        return ordinal === token;
      });
    } else if (typeof token === 'string') {
      return _4.find(db, ({ key }) => {
        return key === token;
      });
    } else if (!token) {
      return undefined;
    } else {
      return _4.find(db, ({ value }) => {
        return _4.isEqual(token, value);
      });
    }
  }

  isEqual(first: token_type<A>, second: token_type<A>): boolean {
    const unsafeSecond: IStrategyRecord<A> | undefined = Strategy._getByUnsafe<A>(this.__db, second);
    const unsafeFirst: IStrategyRecord<A> | undefined = Strategy._getByUnsafe<A>(this.__db, first);

    return !!unsafeFirst && !!unsafeSecond && _4.isEqual(unsafeFirst, unsafeSecond);
  }

  executionContext(token: token_type<A>): IStrategyExecutionContext<A> {
    return new StrategyExecutionContext(this._getBy(token).value);
  }

  insert(key: string, value: A): this {
    return this.__insert(key, value);
  }

  private __insert(key: string, value: A): this {
    const ordinal: number = this.size();

    this.__db[key] = {
      ordinal,
      value,
      key
    };

    this[key] = value;
    return this;
  }

  replace(key: string, value: A): this {
    this.remove(key);
    this.insert(key, value);
    return this;
  }

  move(oldKey: string, newKey: string): this {
    if (!!this.__db[newKey]) {
      return this;
    }

    this.__db[newKey] = this.__db[oldKey];
    this[newKey] = this[oldKey];
    delete this.__db[oldKey];
    delete this[oldKey];
    return this;
  }

  remove(key: string): this {
    delete this.__db[key];
    delete this[key];
    return this;
  }

  clear(): this {
    _4.each(this, (value, key: string) => {
      this.remove(key);
    });

    delete this.__fallback;
    this.__db = {};
    return this;
  }

  fallback(strategy: string | A): this {
    const value: A = typeof strategy === 'string' ? this.__db[strategy].value : strategy;

    this.__fallback = {
      ordinal: Number.MIN_VALUE,
      key: 'fallback',
      value
    };

    return this;
  }

  private static _hideProperty(object: Object, key: string): void {
    const descriptor = Object.getOwnPropertyDescriptor(object, key);

    if (!!descriptor) {
      descriptor.enumerable = false;
      Object.defineProperty(object, key, descriptor);
    }
  }

  map<B>(func: (value: A, key?: string) => B): B[] {
    return _4.map(this.__db, (element) => {
      return func(element.value, element.key);
    });
  }
}

export class StrategySafe<A, S extends string> extends Strategy<A> implements IStrategySafe<A, S> {
  constructor(keys: Record<S, A>) {
    super(keys as any);
  }

  insert(key: S, value: A): this {
    throw new Error('rules are frozen');
  }
  remove(key: S): this {
    throw new Error('rules are frozen');
  }
  clear(): this {
    throw new Error('rules are frozen');
  }
}



// WEBPACK FOOTER //
// ./app/vendor/utility-ts-strategy/src/lib/strategy.ts